import { Flex, Heading, LoadingDots, Text } from '@highereducation/hen-ui';
import { FC } from 'react';
import {
  DEFAULT_LOADING_COPY,
  MAX_LOADING_STEPS,
  type LoadingStep,
  type LoadingStepIconKey,
} from './defaults';

export {
  DEFAULT_LOADING_COPY,
  MAX_LOADING_STEPS,
  type LoadingCopy,
  type LoadingStep,
  type LoadingStepIconKey,
} from './defaults';

export interface LoadingProps {
  title?: string;
  statusLabel?: string;
  steps?: LoadingStep[];
}

const Loading: FC<LoadingProps> = ({
  title = DEFAULT_LOADING_COPY.title,
  statusLabel = DEFAULT_LOADING_COPY.statusLabel,
  steps = DEFAULT_LOADING_COPY.steps,
}) => {
  const visibleSteps = steps.length > 0 ? steps : DEFAULT_LOADING_COPY.steps;

  return (
    <Flex align='center' className='container pt-10 text-center' gap='xl'>
      <Heading
        align='center'
        className='mx-14 my-2 max-w-xl md:mx-auto md:my-4 md:mb-6'
        variant='h2'
      >
        {title}
      </Heading>
      <div className='my-6 md:my-8'>
        <LoadingDots color='secondary' label={statusLabel} />
      </div>
      <div className='mx-auto grid max-w-4xl grid-cols-1 gap-2 md:grid-cols-3'>
        {visibleSteps.slice(0, MAX_LOADING_STEPS).map((step, index) => {
          const fallbackIcon = DEFAULT_LOADING_COPY.steps[index]?.icon;
          return (
            <LoadingStepItem
              key={`${step.label}-${index}`}
              icon={step.icon ?? fallbackIcon ?? 'compare'}
            >
              {step.label}
            </LoadingStepItem>
          );
        })}
      </div>
    </Flex>
  );
};

const LoadingStepItem: FC<{
  children: string;
  icon: LoadingStepIconKey;
}> = ({ children, icon }) => {
  return (
    <div className='flex flex-col items-center justify-between gap-4 px-12 md:flex-col md:justify-start'>
      <LoadingStepIcon icon={icon} />
      <div className='mt-3 mb-7 flex flex-row space-x-1 p-1 text-center md:flex-col md:space-x-0 md:p-0'>
        <Text align='center'>{children}</Text>
      </div>
    </div>
  );
};

const LoadingStepIcon: FC<{ icon: LoadingStepIconKey }> = ({ icon }) => {
  if (icon === 'checklist') {
    return (
      <svg
        data-loading-step-icon={icon}
        xmlns='http://www.w3.org/2000/svg'
        width='48'
        height='48'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2' />
        <rect x='8' y='2' width='8' height='4' rx='1' ry='1' />
        <path d='m9 14 2 2 4-4' />
      </svg>
    );
  }

  if (icon === 'apply') {
    return (
      <svg
        data-loading-step-icon={icon}
        xmlns='http://www.w3.org/2000/svg'
        width='48'
        height='48'
        viewBox='0 0 24 24'
        fill='none'
        stroke='currentColor'
        strokeWidth='2'
        strokeLinecap='round'
        strokeLinejoin='round'
      >
        <path d='M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4' />
        <polyline points='10 17 15 12 10 7' />
        <line x1='15' y1='12' x2='3' y2='12' />
      </svg>
    );
  }

  return (
    <svg
      data-loading-step-icon={icon}
      xmlns='http://www.w3.org/2000/svg'
      width='48'
      height='48'
      viewBox='0 0 24 24'
      fill='none'
      stroke='currentColor'
      strokeWidth='2'
      strokeLinecap='round'
      strokeLinejoin='round'
    >
      <circle cx='10.5' cy='7' r='5' />
      <circle cx='10.5' cy='7' r='1' />
      <path d='M3.5 12h2l1.5 5L10.5 12l3 5 1.5-5h2' />
    </svg>
  );
};

export default Loading;
